import React, { useMemo, useEffect, useRef } from "react";
import { BnbLabel } from "../../../../utils/labels";
import Preloader from "../../../../../../../common-deprecated/styles/lexus/Preloader";
import useSpin, { DEFAULT_FRAME } from "../../../../hooks/customize/useSpin";
import * as Flex from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Flex";
import * as SpinStyles from "./styles/Spin";
import * as Styles from "./styles/ExteriorSpinStyle";
import IconRefresh from "../../../../svg/lexus/refresh.svg";
import useSnowy from "../../../../hooks/customize/winterwheels/useSnowy";
import { useBnbSelector } from "../../../../redux/store";
import { hasWinterWheelInBasket } from "../../../../utils/typedUtils";
import { trackCustomizeExt360Spin } from "../../../../utils/tracking";

type ExteriorSpinType = {
    isFullscreen?: boolean;
};

const ExteriorSpin = (props: ExteriorSpinType): JSX.Element => {
    const { isFullscreen } = props;
    const { frameProps, showWinterWheel, cacheKey, getImageSrc } = useSpin("FFFFFF");
    const { isActive, containerProps, currentFrame, showPrevImg, imageCache, previousImg, isLoading } = frameProps;
    const hasWinterWheel = useBnbSelector((state) => hasWinterWheelInBasket(state.basket));
    const { grade, model } = useBnbSelector((state) => state.car);
    const { colour } = useBnbSelector((state) => state.basket);

    useSnowy(hasWinterWheel, showWinterWheel);

    // Track the first time the user changed the car angle
    const anyCarWasSpun = useRef(false);
    useEffect(() => {
        if (!anyCarWasSpun.current && frameProps.currentFrame !== DEFAULT_FRAME) {
            anyCarWasSpun.current = true;
            trackCustomizeExt360Spin();
        }
    }, [frameProps.currentFrame]);

    return useMemo(
        () => (
            <Styles.Wrapper>
                <Styles.SnowCanvas id="snow" />
                <Flex.Col width={1} {...containerProps}>
                    <Styles.SpinPlayer isFullscreen={isFullscreen}>
                        {!isActive && !isLoading && (
                            <SpinStyles.CenterLabel hasText>
                                <IconRefresh width="16" height="16" />
                                <BnbLabel label="spinDragToRotate" />
                            </SpinStyles.CenterLabel>
                        )}
                        {isLoading && <Preloader delayedVisibility />}
                        {previousImg.src && (
                            <Styles.SpinImage
                                key={`${previousImg.cacheKey}-${previousImg.frame}`}
                                src={previousImg.src}
                                srcSet={previousImg.srcSet}
                                opacity={showPrevImg ? 1 : 0}
                                transition={!showPrevImg ? "opacity 0.3s" : "none"}
                                zindex={5}
                                alt={`${model.name} ${grade.name} ${colour.name}, frame ${previousImg.frame}`}
                            />
                        )}

                        {imageCache.current
                            .filter((image) => image.loaded || image.loading)
                            .map((image) => (
                                <Styles.SpinImage
                                    key={cacheKey + image.frame}
                                    {...getImageSrc(image.frame)}
                                    alt={`${model.name} ${grade.name} ${colour.name}, frame ${image.frame}`}
                                    opacity={image.frame === currentFrame && !showPrevImg ? 1 : 0}
                                />
                            ))}
                    </Styles.SpinPlayer>
                </Flex.Col>
            </Styles.Wrapper>
        ),
        [showPrevImg, currentFrame, isLoading, isActive, hasWinterWheel, isFullscreen],
    );
};

export default ExteriorSpin;
